import { Component, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import {
  ParquetImportService,
  FileImportResult,
} from '../services/parquet-import/parquet-import.service';

/**
 * Interface for displaying import results in the UI
 */
interface ImportResultDisplay {
  fileName?: string;
  importedCount: number;
  skippedCount: number;
  failedCount: number;
}

@Component({
  selector: 'app-parquet-import',
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatListModule,
    MatIconModule,
    MatDividerModule
],
  templateUrl: './parquet-import.component.html',
  styleUrl: './parquet-import.component.scss',
})
export class ParquetImportComponent implements OnInit {
  prefix = '';
  useEmulator = false;
  isImporting = false;
  importResult: ImportResultDisplay | null = null;
  fileList: string[] = [];
  isLoadingFiles = false;
  selectedFile: string | null = null;

  constructor(private readonly importService: ParquetImportService) {}

  ngOnInit(): void {
    this.importService.isImporting$.subscribe(
      (importing) => (this.isImporting = importing),
    );
    this.loadFileList();
  }

  /**
   * Imports all parquet files from the bucket.
   */
  importAll(): void {
    this.importResult = null;
    this.importService.importAll(this.useEmulator).subscribe({
      next: () => {
        this.loadFileList(); // Refresh file list after import
      },
      error: () => {
        // Error handling is done in the service
      },
    });
  }

  /**
   * Imports parquet files filtered by prefix.
   */
  importWithPrefix(): void {
    this.importResult = null;
    this.importService
      .importWithPrefix(this.prefix || undefined, this.useEmulator)
      .subscribe({
        next: () => {
          this.loadFileList(); // Refresh file list after import
        },
        error: () => {
          // Error handling is done in the service
        },
      });
  }

  /**
   * Imports a single selected file.
   */
  importSelectedFile(): void {
    if (!this.selectedFile) {
      return;
    }
    const fileName = this.selectedFile;
    this.importResult = null;
    this.importService.importFile(fileName).subscribe({
      next: (result: FileImportResult) => {
        // Store structured data instead of HTML
        this.importResult = {
          fileName: fileName,
          importedCount: result.importedCount,
          skippedCount: result.skippedCount,
          failedCount: result.failedCount,
        };
        this.loadFileList(); // Refresh file list after import
      },
      error: () => {
        // Error handling is done in the service
      },
    });
  }

  /**
   * Loads the list of parquet files from the bucket.
   */
  loadFileList(): void {
    this.isLoadingFiles = true;
    this.importService.listFiles(this.prefix || undefined).subscribe({
      next: (files) => {
        this.fileList = files;
        this.isLoadingFiles = false;
      },
      error: () => {
        this.isLoadingFiles = false;
      },
    });
  }

  /**
   * Clears the import result.
   */
  clearResult(): void {
    this.importResult = null;
  }

  /**
   * Handles keyboard events on file list items for accessibility.
   * Allows selection using Enter or Space keys.
   */
  onFileItemKeyDown(event: KeyboardEvent, file: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectedFile = file;
    }
  }
}
